import {ACTIVITY_TYPE, AllActivityTypes, AllActivityTypesArray} from "../../types-enums/activity";
import {i18n} from 'i18next'
import {
    isActivityCarBusState,
    isActivityFlightState,
    isActivityHotelState,
    isCalculatedCarBusEmissionsResponse,
    isFlightCalculatedEmissionsResponse,
    isHotelCalculatedEmissionsResponse
} from "../check/type-guards";
import {HotelCalculatedEmissionsResponse} from "../../api/model/emissions";
import {
    CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL,
    CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL
} from "../../api/model/common";
import {generateProfessionalFlightRouteWithoutCo2KgEStrings} from "./flight";
import {getActivityPersonCount, getReturnActivity} from "../getter/activities";
import {allCountryNamesAlpha2CountryCode} from "../../components/settings/utils";
import {
    generateCarBusCalculationTierHintString,
    generateCarBusRouteFromWaypointsString,
    generateCarBusString,
    generateTranslatedBusCategoryString,
    generateTranslatedCarCategoryString
} from "./car-bus";
import {LANGUAGE_ABBREVATION} from "../../locales/locales";
import {getTotalCo2KgEEmissionsAndCalculationStatus} from "../getter/emissions";
import {roundCo2E, roundCo2EPerPerson, roundNumberToOneDigit} from "../math/round-values";
import {FUEL_TYPE, VEHICLE_TYPE} from "../../api/model/activity-car-bus";
import {activityHasReturnOption} from "../check/activities";
import {DateTime} from "luxon";
import {KG_CO2, KM, KW_H, LITER} from "./units";

export const PLACEHOLDER_EMPTY_STRING = '...'
export const NO_DATA_STRING = "--"
export const MORE_DATA_STRING = "..."
export const ADDITIONAL_FLIGHT_INFORMATION_SEPARATOR = " / "
export const COMMA_SEPARATOR = ', '
export const ENUMERATION_CHARACTER = "-"
export const ROUTE_SEPARATOR = ' --- '
export const FLIGHT_CALCULATION_HINT = '*'
export const CAR_BUS_CALCULATION_HINT = '••'

/**
 * Converts the first character of a given string to uppercase
 * and keeps the remaining characters unchanged.
 *
 * @param {string} str - The input string to process.
 * @returns {string} A new string with the first letter capitalized.
 */
export const capitalizeFirstLetter = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1)

/**
 * Converts a given string to lowercase conditionally based on the resolved language from the provided i18n object.
 *
 * @param {string | null | undefined} word - The input string that may be transformed based on the language.
 * @param {i18n} i18n - The internationalization object containing language settings.
 * @returns {string | null} Returns the lowercase version of the input string if the resolved language is English.
 *                          Returns the input string unchanged for other languages.
 *                          Returns null if the input string is null or undefined.
 */
export const conditionallyToLowerCase = (word: string | null | undefined, i18n: i18n): string | null => {
    if (word === null || typeof word === 'undefined') return null
    const {resolvedLanguage} = i18n
    if (resolvedLanguage === LANGUAGE_ABBREVATION.en) return word.toLowerCase()
    return word
}

/**
 * Removes the trailing slash from a given pathname, if it exists.
 *
 * @param {string} pathname - The input string representing a pathname.
 * @returns {string} A string without a trailing slash if one was present, otherwise returns the original string.
 */
export const cleanPathName = (pathname: string): string => pathname.endsWith('/') ? pathname.slice(0, -1) : pathname

/**
 * Converts a given number or string value to its string representation.
 *
 * @param {number|string} value - The value to be converted to a string.
 * @returns {string} The string representation of the input value.
 */
export const toString = (value: number | string): string => String(value)

/**
 * Generates a translated activity type string based on the provided activity type and the i18n translation instance.
 *
 * @param {ACTIVITY_TYPE|string} activityType - Specifies the activity type which can either be a string or an enum value of ACTIVITY_TYPE.
 * @param {i18n} i18n - The internationalization instance used for retrieving the translated string.
 * @returns {string} The translated activity type string based on the provided activityType and the active language configuration in i18n.
 */
export const generateTranslatedActivityTypeString = (activityType: ACTIVITY_TYPE | string, i18n: i18n): string => {
    let activityTypeString = ''
    const {t} = i18n
    if (typeof activityType === 'string') {
        activityTypeString = activityType
    } else {
        activityTypeString = ACTIVITY_TYPE[activityType]
    }
    return t(`ACTIVITIES.${activityTypeString.toUpperCase()}.TITLE`, {
        interpolation: {
            escapeValue: false
        }
    })
}

/**
 * Generates a localized string indicating the emissions calculation tier for a given activity type.
 *
 * @param {AllActivityTypes} activity - The activity object containing details such as type, emissions data, and related properties.
 * @param {i18n} i18n - The internationalization object used for retrieving translations.
 * @returns {string|null} The generated localized string based on the activity type and emissions data, or `null` if the calculation tier string is not applicable.
 *
 * @description
 * This function evaluates the `activityType` of the provided activity and determines the appropriate emissions calculation tier string based on the available emissions data. It supports the following activity types:
 *
 * - `flight`: Handles flight-related activities and generates a hint string based on emission precision and possible improvements.
 * - `hotel`: Calculates tier information for hotel stays, including scope-specific details.
 * - `car_bus`: Generates tier and improvement strings for car and bus-related activities.
 *
 * The function also considers whether the emissions data is partial or failed to determine the outcome. If the activity type is not recognized or the necessary conditions are not met, the function returns `null`.
 */
export const generateActivityEmissionsCalculationTierString = (activity: AllActivityTypes, i18n: i18n): string | null => {
    const {t} = i18n
    const {common: {totalEmissions, activityType}} = activity
    if (totalEmissions !== null) {
        const {status} = totalEmissions[0]
        switch (activityType) {
            case ACTIVITY_TYPE.flight:
                if (isActivityFlightState(activity) && totalEmissions.every(isFlightCalculatedEmissionsResponse)) {
                    const {legs} = activity
                    const {showFootnote} = generateProfessionalFlightRouteWithoutCo2KgEStrings(legs, i18n)
                    if (status !== CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL.failed) {
                        if (showFootnote) return t("HINTS.EMISSION_CALCULATION.FLIGHT.BETTER_CALCULATION_POSSIBILITY_GENERAL")
                        return t("HINTS.EMISSION_CALCULATION.FLIGHT.PRECISE_CALCULATED")
                    }
                    return null
                }
                return null
            case ACTIVITY_TYPE.hotel:
                if (isActivityHotelState(activity) && totalEmissions.every(isHotelCalculatedEmissionsResponse)) {
                    const {scope} = totalEmissions[0] as HotelCalculatedEmissionsResponse
                    if (status !== CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL.failed) return t("HINTS.EMISSION_CALCULATION.HOTEL.TEXT", {tier: t(`HINTS.EMISSION_CALCULATION.HOTEL.${scope}`)})
                    return null
                }
                return null
            case ACTIVITY_TYPE.car_bus:
                if (isActivityCarBusState(activity) && totalEmissions.every(isCalculatedCarBusEmissionsResponse)) {
                    const {tierMessage, improvements} = generateCarBusCalculationTierHintString(activity, i18n)
                    return `${tierMessage} ${improvements}`
                }
                return null
            default:
                activityType satisfies never
        }
    }
    return null
}

/**
 * Constructs a required label string by appending an asterisk (*)
 * to the provided label text.
 *
 * @param {string} label - The label text to which the asterisk will be appended.
 * @returns {string} The label text followed by an asterisk (*) indicating it is required.
 */
export const constructRequiredLabelString = (label: string): string => `${label}*`
/**
 * Formats a numerical value representing the quantity of petrol/diesel into a localized string with the unit "LITER".
 *
 * This function rounds the input value to one decimal place, converts it into a locale-specific string format
 * based on the provided `resolvedLanguage`, appends the unit "LITER", and trims any unnecessary whitespaces.
 *
 * @param {number} petrolDieselL - The numeric value representing the quantity of petrol/diesel in liters.
 * @param {string | undefined} resolvedLanguage - The resolved language code used for locale-specific formatting.
 *     If undefined, the default locale of the environment will be used.
 * @returns {string} The formatted string representing the quantity with the "LITER" unit.
 */
export const formatPetrolDieselString = (petrolDieselL: number, resolvedLanguage: string | undefined): string => `${roundNumberToOneDigit(petrolDieselL).toLocaleString(resolvedLanguage)} ${LITER}`.trim()
/**
 * Formats a given kilowatt-hour (kWh) value into a localized string with a unit suffix.
 *
 * @param {number} kwH - The kilowatt-hour value to be formatted.
 * @param {string | undefined} resolvedLanguage - The optional locale identifier for formatting the number.
 * @returns {string} The formatted string with the kWh value rounded to one decimal place,
 * localized according to the resolvedLanguage, and followed by the unit suffix.
 */

export const formatKwHString = (kwH: number, resolvedLanguage: string | undefined): string => `${roundNumberToOneDigit(kwH).toLocaleString(resolvedLanguage)} ${KW_H}`.trim()
/**
 * Formats a given distance in kilometers into a localized string representation.
 *
 * @param {number} distanceKm - The distance in kilometers to format.
 * @param {string | undefined} resolvedLanguage - The language code specifying the locale for formatting. If undefined, the default locale is used.
 * @returns {string} A formatted string representing the distance, rounded to one decimal place, localized to the specified language, and appended with the unit "KM".
 */
export const formatDistanceKmString = (distanceKm: number, resolvedLanguage: string | undefined): string => `${roundNumberToOneDigit(distanceKm).toLocaleString(resolvedLanguage)} ${KM}`.trim()
/**
 * Formats the total CO2e value into a readable string.
 *
 * @param {number} co2E - The CO2 equivalent value to be formatted.
 * @param {string | undefined} resolvedLanguage - The language or locale in which the CO2e value should be localized.
 * @returns {string} The formatted string representing the CO2e value with localization and units.
 */
export const formatCo2ETotalString = (co2E: number, resolvedLanguage: string | undefined): string => `${roundCo2E(co2E).toLocaleString(resolvedLanguage)} ${KG_CO2}`.trim()
/**
 * Formats the CO2 emissions per person as a localized string with the appropriate unit.
 *
 * @param {number} co2ETotal - The total CO2 emissions.
 * @param {number} countPerson - The number of people to divide the total emissions by.
 * @param {string | undefined} resolvedLanguage - The language tag for localization, or undefined to use the default language.
 * @returns {string} A formatted string representing the CO2 emissions per person with appropriate localization and unit.
 */
export const formatCo2EPerPersonString = (co2ETotal: number, countPerson: number, resolvedLanguage: string | undefined): string => `${roundCo2EPerPerson(co2ETotal, countPerson).toLocaleString(resolvedLanguage)} ${KG_CO2}`.trim()
/**
 * Generates a day description string based on the given day count and internationalization configuration.
 *
 * @param {number} dayCount - The number of days to be represented in the string.
 * @param {object} i18n - The internationalization object providing translation functions.
 * @param {Function} i18n.t - A function used to retrieve translated text for given keys.
 * @returns {string} - A formatted string representing the number of days, properly pluralized and localized.
 *                   -> 2 Days or 1 Day
 */
export const generateDayString = (dayCount: number, i18n: i18n): string => {
    const {t} = i18n
    if (dayCount > 1) return `${dayCount} ${t("GENERAL.DAYS")}`
    return `${dayCount} ${t("GENERAL.DAY")}`
}
/**
 * Constructs a localized string representing CO2 emissions per person.
 *
 * @param {number} co2ETotal - The total CO2 emissions.
 * @param {number} countPerson - The number of persons to divide the CO2 emissions by.
 * @param {string | undefined} resolvedLanguage - The resolved language for localization.
 * @param {i18n} i18n - The internationalization object used for translation.
 * @returns {string} - A formatted string that includes the CO2 emissions per person and the localized "per person" label.
 *                   -> 71 kg CO₂e per person
 */
export const generateCo2EPerPersonString = (co2ETotal: number, countPerson: number, resolvedLanguage: string | undefined, i18n: i18n): string => `${formatCo2EPerPersonString(co2ETotal, countPerson, resolvedLanguage)} ${i18n.t("GENERAL.PER_PERSON")}`
/**
 * Generates a string representing the total CO2 emissions and CO2 emissions per person.
 *
 * This function combines the total CO2 emissions and emissions per person into a formatted string,
 * using localization and proper formatting.
 *
 * @param {number} co2ETotal - The total CO2 emissions.
 * @param {number} countPerson - The number of people considered for calculating emissions per person.
 * @param {string | undefined} resolvedLanguage - The language code to be used for localization. If undefined, a default language may be used.
 * @param {i18n} i18n - The internationalization instance used for translating and formatting strings.
 * @returns {string} - A formatted string containing the total CO2 emissions and emissions per person.
 *                   -> Total: 71 kg CO₂e (71 kg CO₂e per person)
 */
export const generateTotalCo2EAndCo2EPerPersonString = (co2ETotal: number, countPerson: number, resolvedLanguage: string | undefined, i18n: i18n): string => `${i18n.t("GENERAL.TOTAL")}: ${formatCo2ETotalString(co2ETotal, resolvedLanguage)} (${generateCo2EPerPersonString(co2ETotal, countPerson, resolvedLanguage, i18n)})`
/**
 * Formats a given date into a localized string representation based on the specified parameters.
 *
 * @param {number | DateTime} dateToFormat - The date to be formatted. Accepts either a timestamp in milliseconds or a Luxon DateTime object.
 * @param {string | undefined} resolvedLanguage - The locale to resolve the string formatting. If undefined, defaults to the system locale.
 * @param {boolean} shortDate - Determines whether the formatted date should use a short (numeric) or long (full) month representation.
 * @returns {string} A formatted date string localized according to the provided language and format preferences.
 */
export const formatDateString = (dateToFormat: number | DateTime, resolvedLanguage: string | undefined, shortDate: boolean): string => {
    let date: DateTime
    if (typeof dateToFormat === 'number') {
        date = DateTime.fromMillis(dateToFormat).toLocal()
    } else {
        date = dateToFormat
    }
    return date.toLocaleString({
        day: '2-digit',
        month: shortDate ? "numeric" : 'long',
        year: 'numeric',
    }, {locale: resolvedLanguage})
}

/**
 * Removes instances of the word "undefined" from the given string along with any surrounding whitespace.
 * The function also trims any leading or trailing whitespace from the final result.
 *
 * @param {string} input - The input string from which occurrences of "undefined" should be removed.
 * @returns {string} - A new string with all instances of "undefined" and surrounding whitespace removed.
 */
export const removeUndefinedFromString = (input: string): string => input.replace(/\s*undefined\s*/g, '').trim()
/**
 * Generates a fuel consumption string based on the provided fuel type, consumption value, and language.
 *
 * @param {FUEL_TYPE | null} fuelType - The type of fuel being consumed. Can be null, indicating a default behavior for petrol/diesel.
 * @param {number} fuelConsumption - The amount of fuel consumed. Represents liters for petrol/diesel or kWh for electric.
 * @param {string | undefined} resolvedLanguage - The language to be used for formatting the output string. If undefined, a default language is used.
 * @returns {string} A formatted string representing the fuel consumption in the context of the given fuel type and language.
 */
export const generateFuelConsumptionString = (fuelType: FUEL_TYPE | null, fuelConsumption: number, resolvedLanguage: string | undefined) => {
    if (fuelType === null) return formatPetrolDieselString(fuelConsumption, resolvedLanguage)
    switch (fuelType) {
        case FUEL_TYPE.unknown:
            return formatPetrolDieselString(fuelConsumption, resolvedLanguage)
        case FUEL_TYPE.diesel:
            return formatPetrolDieselString(fuelConsumption, resolvedLanguage)
        case FUEL_TYPE.petrol:
            return formatPetrolDieselString(fuelConsumption, resolvedLanguage)
        case FUEL_TYPE.default:
            return formatPetrolDieselString(fuelConsumption, resolvedLanguage)
        case FUEL_TYPE.electric:
            return formatKwHString(fuelConsumption, resolvedLanguage)
        default:
            fuelType satisfies never;
    }
}

export enum WhereToDisplayActivityString {
    travelComponents, journey, pdf, dropdownJourney
}

/**
 * Generates a string converted to lowercase based on the specified language.
 *
 * The function takes a resolved language and a string as inputs and determines whether or not
 * to convert the string to lowercase depending on the language. If the language matches certain
 * conditions or is undefined, the function returns the input string unmodified or converted.
 *
 * @param {string | undefined} resolvedLanguage - The language code used to determine the behavior of the conversion.
 *                                                It is expected to be one of the defined `LANGUAGE_ABBREVATION` values or undefined.
 * @param {string | null | undefined} toLowerCaseString - The string to potentially convert to lowercase.
 *                                                        If null or undefined, the function returns it as-is.
 * @returns {string | null | undefined} - The processed string, depending on the input language and its casing requirements.
 */
export const generateToLowerCaseDependingOnLanguageString = (resolvedLanguage: string | undefined, toLowerCaseString: string | null | undefined): string | null | undefined => {
    if (resolvedLanguage) {
        const resolvedLanguageEnum = resolvedLanguage as LANGUAGE_ABBREVATION
        switch (resolvedLanguageEnum) {
            case LANGUAGE_ABBREVATION.de:
                return toLowerCaseString
            case LANGUAGE_ABBREVATION.en:
                if (toLowerCaseString) return toLowerCaseString.toLowerCase()
                return toLowerCaseString
            default:
                resolvedLanguageEnum satisfies never
        }
    }
    return toLowerCaseString
}
/**
 * Generates a localized string representing CO2 emissions per person and total emissions.
 *
 * @param {number} co2E - Total CO2 emissions.
 * @param {number} numberOfPeople - Number of people to calculate per-person emissions.
 * @param {Object} i18n - Internationalization object containing translation functions and resolved language.
 * @param {function} i18n.t - Function to fetch localized text based on translation keys.
 * @param {string} i18n.resolvedLanguage - The currently resolved language for localization.
 * @returns {Object} Returns an object containing:
 *   - `co2EPerPersonString` (string): Localized string for per-person CO2 emissions.
 *   - `co2ETotalString` (string): Localized string for total CO2 emissions.
 *   -> {"co2EPerPersonString":"per person 71 kg CO₂e","co2ETotalString":"(Total 71 kg CO₂e)"}
 */
export const generateCo2KgEPerPersonString = (co2E: number, numberOfPeople: number, i18n: i18n) => {
    const {t, resolvedLanguage} = i18n
    const co2EPerPerson = `${formatCo2EPerPersonString(co2E, numberOfPeople, resolvedLanguage)}`
    const co2EPerPersonString = `${t('GENERAL.PER_PERSON')} ${co2EPerPerson}`
    const co2ETotalString = `(${t('GENERAL.TOTAL')} ${formatCo2ETotalString(co2E, resolvedLanguage)})`
    return {co2EPerPersonString, co2ETotalString}
}

/**
 * Generates a flight type string based on the number of flights and whether the flight is a return trip.
 *
 * @param {number} flightCount - The number of flights.
 * @param {boolean} returnFlight - Indicates if the flight is a return trip (true) or one-way (false).
 * @returns {string} A string identifier for the flight type, tailored for singular or plural and one-way or round trip.
 */
export const generateFlightTypeString = (flightCount: number, returnFlight: boolean,): string => {
    if (flightCount === 1) {
        if (returnFlight) return 'ACTIVITIES.FLIGHT.ROUNDTRIP_SINGULAR'
        return 'ACTIVITIES.FLIGHT.ONEWAY_SINGULAR'
    } else {
        if (returnFlight) return 'ACTIVITIES.FLIGHT.ROUNDTRIP_PLURAL'
        return 'ACTIVITIES.FLIGHT.ONEWAY_ONLY_PLURAL'
    }
}

/**
 * Generates a string representation for the passenger count.
 *
 * This function returns a singular or plural form based on the
 * number of passengers provided. If the count is exactly 1,
 * it returns the string 'GENERAL.PASSENGER'. For any other count,
 * it returns the string 'GENERAL.PASSENGERS'.
 *
 * @param {number} passengerCount - The number of passengers.
 * @returns {string} A string representing the passenger count in singular or plural form.
 */
export const generatePassengerString = (passengerCount: number, i18n: i18n): string => {
    const {t} = i18n
    if (passengerCount === 1) return t('GENERAL.PASSENGER')
    return t('GENERAL.PASSENGERS')
}

/**
 * Generates a string identifier based on the number of guests.
 *
 * This function returns a string key that corresponds to whether there is a single guest
 * or multiple guests. It is commonly used for localization or retrieving the appropriate
 * string representation for the provided guest count.
 *
 * @param {number} guestCount - The number of guests.
 * @returns {string} A string key ('GENERAL.GUEST' for one guest,
 *                    'GENERAL.GUESTS' for multiple guests).
 */
export const generateGuestString = (guestCount: number, i18n: i18n): string => {
    const {t} = i18n
    if (guestCount === 1) return t('GENERAL.GUEST')
    return t('GENERAL.GUESTS')
}

/**
 * Generates a translated string indicating the number of nights based on the given number of nights.
 *
 * @param {number} numberOfNights - The number of nights for which the string is to be generated.
 * @param {Object} i18n - The internationalization object providing the translation method.
 * @param {Function} i18n.t - The translation function to retrieve localized strings.
 * @returns {string} A translated string for "night" or "nights" based on the provided number of nights.
 */
export const generateNumberOfNightsString = (numberOfNights: number, i18n: i18n): string => {
    const {t} = i18n
    return numberOfNights === 1
        ? `${t("SCREENS.TRAVEL_COMPONENTS_OVERVIEW.NIGHT")}`
        : `${t("SCREENS.TRAVEL_COMPONENTS_OVERVIEW.NIGHTS")}`
}

/**
 * Generates a string identifier based on the number of persons.
 *
 * This function returns a string key that corresponds to whether there is a single person
 * or multiple persons. It is commonly used for localization or retrieving the appropriate
 * string representation for the provided person count.
 *
 * @param {number} personCount - The number of guests.
 * @returns {string} A string key ('GENERAL.PERSON' for one person,
 *                    'GENERAL.PERSONS' for multiple persons).
 */
export const generatePersonCountString = (personCount: number, i18n: i18n): string => {
    const {t} = i18n
    if (personCount === 1) return t('GENERAL.PERSON')
    return t('GENERAL.PERSONS')
}


/**
 * Generates a formatted string containing the total number of people, the CO2 equivalent per person,
 * and the total CO2 equivalent in kilograms.
 *
 * @param {number} co2E - The total CO2 equivalent in kilograms.
 * @param {number} numberOfPeople - The total number of people.
 * @param {i18n} i18n - The internationalization object for translations.
 * @returns {string} - A formatted string including the total number of people, the CO2 equivalent per person, and the total CO2 equivalent.
 *                   -> 1 Person,  per person 71 kg CO₂e (Total 71 kg CO₂e)
 */
export const generateTotalAndCo2KgEPerPersonString = (co2E: number, numberOfPeople: number, i18n: i18n) => {
    const {
        co2EPerPersonString,
        co2ETotalString
    } = generateCo2KgEPerPersonString(co2E, numberOfPeople, i18n)
    const totalNumberOfPeopleString = `${numberOfPeople} ${generatePassengerString(numberOfPeople, i18n)}`
    return `${totalNumberOfPeopleString}${COMMA_SEPARATOR} ${co2EPerPersonString} ${co2ETotalString}`
}

export const generateFlightCountString = (flightCount: number) => {
    if (flightCount === 1) return "ACTIVITIES.FLIGHT.FLIGHT_SINGULAR"
    return "ACTIVITIES.FLIGHT.FLIGHT_PLURAL"
}

/**
 * Generates and returns CO2 emissions information based on the provided activity, activities list, and localization settings.
 *
 * @param {AllActivityTypes} activity - The activity object containing emission details for a single activity.
 * @param {AllActivityTypesArray} activities - An array of all activity objects, used for retrieving related activity data such as return activities.
 * @param {boolean} co2KgEWithReturnActivity - Determines whether to include the CO2 emissions of the return leg of the activity.
 * @param {i18n} i18n - Localization object with translation and language resolution functions.
 *
 * @returns {{co2KgEPerPersonStrings: string[], totalCo2KgEOfActivity: number}} An object containing:
 *   - `co2KgEPerPersonStrings`: An array of strings representing CO2 emissions per person for each segment.
 *   - `totalCo2KgEOfActivity`: The total CO2 emissions for the activity, including return trip emissions if requested.
 *   -> "708 kg CO₂e p. P."
 */
export const generateCo2KgEForActivityString = (
    activity: AllActivityTypes,
    activities: AllActivityTypesArray,
    co2KgEWithReturnActivity: boolean,
    i18n: i18n
): { co2KgEPerPersonStrings: string[]; totalCo2KgEOfActivity: number; } => {
    const {resolvedLanguage, t} = i18n
    let co2KgEPerPersonStrings: string[] | undefined = []
    let totalCo2KgEOfActivity = 0
    const {common} = activity
    if (common) {
        const {totalEmissions} = common
        const peopleCount = getActivityPersonCount(activity)
        co2KgEPerPersonStrings = totalEmissions?.map((element, index) => {
            const {emissionKgCO2e, status} = element
            if (peopleCount !== null) {
                if (co2KgEWithReturnActivity && activityHasReturnOption(activity)) {
                    const returnActivity = getReturnActivity(activity, activities)
                    if (returnActivity) {
                        const {common: {totalEmissions: totalEmissionsReturnActivity}} = returnActivity
                        if (totalEmissionsReturnActivity) {
                            // make sure to index return leg index from end, because the route is reversed and we need
                            // the sum of Co2 for each leg forth and back
                            const emissionsOfReturnLeg = totalEmissionsReturnActivity.at(index - totalEmissionsReturnActivity.length)
                            if (emissionsOfReturnLeg) {
                                const {
                                    status: statusReturnActivity,
                                    emissionKgCO2e: emissionKgCO2eReturnActivity
                                } = emissionsOfReturnLeg
                                if (status === CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL.success || statusReturnActivity == CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL.success) {
                                    const emissionsOfBothRoutes = roundCo2E(emissionKgCO2e!!) + roundCo2E(emissionKgCO2eReturnActivity!!)
                                    totalCo2KgEOfActivity += emissionsOfBothRoutes
                                    return `${formatCo2EPerPersonString(emissionsOfBothRoutes, peopleCount, resolvedLanguage)} ${t("GENERAL.PER_PERSON_ABBREVATION")}`
                                }
                            }
                        }
                    }
                } else {
                    if (status === CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL.success && emissionKgCO2e !== null) {
                        const emissionKgCO2eRounded = roundCo2E(emissionKgCO2e)
                        totalCo2KgEOfActivity += emissionKgCO2eRounded
                        return `${formatCo2EPerPersonString(emissionKgCO2eRounded, peopleCount, resolvedLanguage)} ${t("GENERAL.PER_PERSON_ABBREVATION")}`
                    }
                }
            }
            return ROUTE_SEPARATOR
        })

    }
    if (typeof co2KgEPerPersonStrings === 'undefined') return {
        co2KgEPerPersonStrings: [NO_DATA_STRING],
        totalCo2KgEOfActivity: 0
    }
    return {co2KgEPerPersonStrings, totalCo2KgEOfActivity}
}

/**
 * Generates a route string based on the provided starting point, destination, and route length.
 *
 * @param {string | number | undefined} start - The starting point of the route. Can be a string, number, or undefined.
 * @param {string | number | undefined} destination - The destination point of the route. Can be a string, number, or undefined.
 * @param {number} routeLength - The length of the route. Determines the separator used in the resulting string.
 * @returns {string} - A formatted string representing the route, including the start, separator or additional data string, and the destination.
 *                   -> BER  ...  JNB
 */
const createRouteString = (start: string | number | undefined, destination: string | number | undefined, routeLength: number): string => `${start} ${routeLength === 2 ? ROUTE_SEPARATOR : ` ${MORE_DATA_STRING} `} ${destination}`

/**
 * Returns the translated Activity type and a short composed title. The composed title is used when an Activity
 * is dragged and dropped. It then replaces the original element for the moment of drag and drop (the element that
 * is shown directly at the cursor of the mouse)
 *
 * This function processes different types of activities (flight, hotel, or car/bus) and retrieves
 * their corresponding short titles and type descriptions according to the provided `i18n` translations.
 * For each activity type, a specific format is applied based on its unique properties and relationships.
 *
 * @param {AllActivityTypes} activity - The activity object containing information such as type and relevant details.
 * @param {i18n} i18n - The internationalization object used for translations.
 * @returns {Object} An object containing:
 *   - `activityString` (string): The short title or description of the activity.
 *   - `activityTypeString` (string): The type of the activity, translated using i18n.
 *   -> {"activityString":"Sofitel Legend Old Cataract Aswan","activityTypeString":"Hotel"}
 *   -> {"activityString":"Manchester: Rockdale Road --- No address found, using coordinates (-74.34493765148285, 44.12501516524597) --- No address found, using coordinates (-71.38008365046403, 43.051597260255335) --- Providence: Murray Street","activityTypeString":"ACTIVITIES.CAR_BUS.CAR"}
 *   -> {"activityString":"BER  ...  JNB","activityTypeString":"Flight"}
 * @throws Will throw an error if an invalid activity type is encountered.
 */
export const getActivityShortTitleAndType = (activity: AllActivityTypes, i18n: i18n) => {
    const {common: {activityType}} = activity
    let activityString = ''
    let activityTypeString = ''
    switch (activityType) {
        case ACTIVITY_TYPE.flight:
            if (isActivityFlightState(activity)) {
                const {legs} = activity
                const firstLeg = legs[0]
                const lastLeg = legs[legs.length - 1]
                activityString = createRouteString(firstLeg?.airport?.iata, lastLeg?.airport?.iata, legs.length)
                activityTypeString = generateTranslatedActivityTypeString("flight", i18n)
            }
            break;
        case ACTIVITY_TYPE.hotel:
            if (isActivityHotelState(activity)) {
                const {city, country, hotel} = activity
                activityTypeString = generateTranslatedActivityTypeString("hotel", i18n)
                if (country) {
                    if (hotel && hotel.name.length > 0) {
                        activityString = hotel.name
                    } else {
                        if (city) {
                            const {name} = city
                            const {alpha2} = country
                            activityString = `${name} (${allCountryNamesAlpha2CountryCode[alpha2]})`
                        }
                    }
                }
            }
            break;
        case ACTIVITY_TYPE.car_bus:
            if (isActivityCarBusState(activity)) {
                let route: string | null = null
                const {
                    vehicleType,
                    fuelConsumption,
                    fuelType,
                    manualEnteredDistanceKm,
                    calculatedDistanceKm,
                    waypoints,
                    busCategory,
                    carCategory,
                } = activity
                if (vehicleType !== null) {
                    activityTypeString = generateCarBusString(vehicleType, i18n) ?? ''
                } else {
                    //fallback
                    activityTypeString = generateTranslatedActivityTypeString("car_bus", i18n)
                }

                if (waypoints.filter(Boolean).length >= 2) route = generateCarBusRouteFromWaypointsString(waypoints, i18n, false)

                if (route) {
                    activityString = route
                } else {
                    const values = [
                        manualEnteredDistanceKm ?? calculatedDistanceKm,
                        fuelConsumption,
                        fuelType,
                        manualEnteredDistanceKm,
                        calculatedDistanceKm,
                    ]
                    activityString = values.filter(value => value !== null).join(", ");
                }
                if (vehicleType === VEHICLE_TYPE.bus && busCategory) activityString = `${generateTranslatedBusCategoryString(i18n, busCategory)} ${activityString}`
                if (vehicleType === VEHICLE_TYPE.car && carCategory) activityString = `${generateTranslatedCarCategoryString(i18n, carCategory)} ${activityString}`

            }
            break;
        default:
            return activityType satisfies never
    }

    return {activityString, activityTypeString}
}

export enum WhatStringToInsert {
    "and",
    "or",
    "andOr",
}

/**
 * Inserts the localized "and", "or", or "and/or" between the last two elements of a string list.
 *
 * @param {string[]} stringList - An array of strings to which the conjunction or disjunction will be added.
 * @param {i18n} i18n - An internationalization object used to retrieve localized words.
 * @param {"and" | "or" | "andOr"} whatToInsert - Specifies which localized word to insert ("and", "or", or "and/or").
 * @returns {string} A single string with the elements of `stringList` joined, with the appropriate localized word added between the last two elements if the list contains multiple items.
 */
export const insertAndOrIntoStringList = (stringList: string[], i18n: i18n, whatToInsert: WhatStringToInsert): string => {
    let word: string = ""
    const {t} = i18n

    switch (whatToInsert) {
        case WhatStringToInsert.and:
            word = t("GENERAL.AND")
            break
        case WhatStringToInsert.or:
            word = t("GENERAL.OR")
            break
        case WhatStringToInsert.andOr:
            word = `${t("GENERAL.AND")}-${t("GENERAL.OR")}`
            break
        default:
            whatToInsert satisfies never
    }

    return stringList.length > 1
        ? stringList.slice(0, -1).join(", ") + ` ${word} ` + stringList[stringList.length - 1]
        : stringList.join("");
}

/**
 * Generates a formatted string by inserting a specified conjunction (e.g., "and" or "or")
 * into a list of strings. The function filters out null values from the input list before
 * processing.
 *
 * @param {Array<string | null>} stringList - An array of strings or null values.
 * @param {i18n} i18n - Localization object used to determine the language-specific conjunction.
 * @param {WhatStringToInsert} whatToInsert - Specifies which conjunction to insert ("and", "or", etc.).
 * @returns {string} A single string with the conjunction inserted between list items.
 */
export const generateStringListWithAndString = (stringList: (string | null)[], i18n: i18n, whatToInsert: WhatStringToInsert): string => insertAndOrIntoStringList(stringList.filter((x): x is string => x !== null), i18n, whatToInsert)

/**
 * Generates a string representing the total CO2 emissions in kilograms equivalent (KgCO2e) or an error message
 * based on the calculation status.
 *
 * @function
 * @param {AllActivityTypes} activity - The activity data used to calculate CO2 emissions.
 * @param {i18n} i18n - The internationalization object containing translation functions and language details.
 * @returns {string} - A formatted string of the total CO2 emissions or a localized error message, depending on the calculation status.
 *                   -> 2389 kg Co2e (partial calculation failure)
 */
export const generateTotalCo2KgEOrFailedString = (activity: AllActivityTypes, i18n: i18n) => {
    const {t, resolvedLanguage} = i18n
    const {status, emissionKgCO2eTotal} = getTotalCo2KgEEmissionsAndCalculationStatus(activity)
    if (emissionKgCO2eTotal === null || status === CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.failed) return t('ERRORS.CALCULATION_COMPLETE_FAILURE')
    if (status === CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.success) return formatCo2ETotalString(roundCo2E(emissionKgCO2eTotal), resolvedLanguage)
    if (status === CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.success_with_errors) return `${formatCo2ETotalString(roundCo2E(emissionKgCO2eTotal), resolvedLanguage)} (${t('ERRORS.CALCULATION_PARTIAL_FAILURE')})`
}